import SVGPathCommander from "svg-path-commander";
import { buildFallbackSvgFromHtmlBody } from "@hightouch/ad-studio-renderer";

const parser = new DOMParser();
const serializer = new XMLSerializer();
const EDITOR_NODE_ID_ATTR = "data-ht-node-id";
const XLINK_NS = "http://www.w3.org/1999/xlink";

/** Convert client (mouse) coordinates to SVG user-space via getScreenCTM. */
export function mouseToSvg(
  svgEl: SVGSVGElement,
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const pt = svgEl.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return { x: clientX, y: clientY };
  const svgPt = pt.matrixTransform(ctm.inverse());
  return { x: svgPt.x, y: svgPt.y };
}

/** Check if two number-array paths are equal. */
export function pathsEqual(
  a: readonly number[],
  b: readonly number[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
let editorNodeIdCounter = 0;

export interface ElementMetadata {
  label?: string;
  fileKey?: string;
  imageUrl?: string;
}

export function createEditorNodeId(): string {
  editorNodeIdCounter += 1;
  return `ht-node-${editorNodeIdCounter.toString(36)}-${crypto
    .randomUUID()
    .slice(0, 8)}`;
}

/** Check if an href value is a storage key (not a resolved URL). */
export function isStorageKey(value: string): boolean {
  return (
    !value.startsWith("http://") &&
    !value.startsWith("https://") &&
    !value.startsWith("data:") &&
    !value.startsWith("/")
  );
}

/** Parse an SVG string into an SVGSVGElement. */
export function parseSvg(str: string): SVGSVGElement {
  const doc = parser.parseFromString(str, "text/html");
  let svg = doc.body.querySelector("svg");
  if (!svg) {
    // Some ad `.svg` files were persisted as raw editor-HTML with no <svg>
    // root (ADS-506). Wrap that markup in <svg><foreignObject> so the ad still
    // opens for repair instead of blanking the layer and crashing the editor.
    const fallback = buildFallbackSvgFromHtmlBody(doc.body);
    if (fallback) {
      svg = parser
        .parseFromString(fallback, "text/html")
        .body.querySelector("svg");
    }
  }
  if (!svg) {
    throw new Error("SVG parse error: no <svg> element found");
  }
  return svg as unknown as SVGSVGElement;
}

/** Serialize an SVG element back to a string. */
export function serializeSvg(el: SVGSVGElement): string {
  return serializer.serializeToString(el);
}

/** Clone an SVG root so edits never mutate history snapshots in place. */
export function cloneSvgRoot(root: SVGSVGElement): SVGSVGElement {
  return root.cloneNode(true) as SVGSVGElement;
}

export function resolveAdRefs(
  root: Element,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string | undefined,
  maxImageDimension?: number,
): void {
  if (!componentAssets || !apiBase) return;

  for (const img of Array.from(root.querySelectorAll("image"))) {
    const href = [
      img.getAttribute("href"),
      img.getAttributeNS(XLINK_NS, "href"),
    ].find((value): value is string => Boolean(value?.startsWith("ad:")));
    if (!href) continue;

    const filename = href.slice(3);
    const s3Key = componentAssets[filename];
    if (!s3Key) continue;

    img.setAttribute("data-ad-ref", href);
    const params = new URLSearchParams({ key: s3Key });
    if (maxImageDimension) {
      params.set("width", String(maxImageDimension));
      params.set("format", "webp");
    }
    img.setAttribute("href", `${apiBase}/agents/files?${params.toString()}`);
    img.removeAttributeNS(XLINK_NS, "href");
  }
}

/**
 * Resolve `<use href="ad:...">` references by fetching SVG content from the
 * API and injecting `<symbol>` definitions into a namespaced `<defs>` block.
 * Returns true if any symbols were injected.
 */
export async function resolveAdUseRefs(
  root: Element,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string | undefined,
): Promise<boolean> {
  if (!componentAssets || !apiBase) return false;

  const getAdHref = (el: Element): string | null => {
    const href = el.getAttribute("href") ?? el.getAttributeNS(XLINK_NS, "href");
    return href?.startsWith("ad:") ? href : null;
  };

  const useElements = Array.from(root.querySelectorAll("use")).filter(
    (el) => getAdHref(el) !== null,
  );
  if (useElements.length === 0) return false;

  const uniqueRefs = new Map<string, string>();
  for (const el of useElements) {
    const href = getAdHref(el)!;
    const filename = href.slice(3);
    if (!uniqueRefs.has(filename)) {
      const s3Key = componentAssets[filename];
      if (s3Key) {
        uniqueRefs.set(filename, s3Key);
      }
    }
  }

  if (uniqueRefs.size === 0) return false;

  const svgContents = new Map<string, string>();
  await Promise.all(
    Array.from(uniqueRefs.entries()).map(async ([filename, s3Key]) => {
      try {
        const params = new URLSearchParams({ key: s3Key, stream: "true" });
        const response = await fetch(
          `${apiBase}/agents/files?${params.toString()}`,
          { credentials: "include" },
        );
        if (!response.ok) {
          console.warn(
            `[resolveAdUseRefs] Failed to fetch "${filename}" (${response.status}).`,
          );
          return;
        }
        const text = await response.text();
        svgContents.set(filename, text);
      } catch (error) {
        console.warn(
          `[resolveAdUseRefs] Error fetching "${filename}"; leaving <use> unresolved.`,
          error,
        );
      }
    }),
  );

  if (svgContents.size === 0) return false;

  const doc = root.ownerDocument;
  const defs = doc.createElementNS("http://www.w3.org/2000/svg", "defs");
  defs.setAttribute("data-ad-use-defs", "true");

  const addedSymbols = new Map<string, string>();

  for (const el of useElements) {
    const href = getAdHref(el)!;
    const filename = href.slice(3);
    const svgText = svgContents.get(filename);
    if (!svgText) continue;

    let symbolId = addedSymbols.get(filename);
    if (!symbolId) {
      symbolId = `ad--${filename.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

      const tempContainer = doc.createElement("div");
      tempContainer.innerHTML = svgText;
      const svgEl = tempContainer.querySelector("svg");
      if (!svgEl) continue;

      const symbol = doc.createElementNS(
        "http://www.w3.org/2000/svg",
        "symbol",
      );
      symbol.setAttribute("id", symbolId);
      const viewBox = svgEl.getAttribute("viewBox");
      if (viewBox) {
        symbol.setAttribute("viewBox", viewBox);
      }
      while (svgEl.firstChild) {
        symbol.appendChild(svgEl.firstChild);
      }
      defs.appendChild(symbol);
      addedSymbols.set(filename, symbolId);
    }

    el.setAttribute("data-ad-ref", href);
    el.removeAttributeNS(XLINK_NS, "href");
    el.setAttribute("href", `#${symbolId}`);
  }

  if (addedSymbols.size > 0) {
    root.appendChild(defs);
  }

  return addedSymbols.size > 0;
}

export function normalizeViewBox(root: SVGSVGElement): {
  width: number;
  height: number;
} {
  if (!root.getAttribute("viewBox")) {
    const w = root.getAttribute("width") || "1080";
    const h = root.getAttribute("height") || "1080";
    root.setAttribute("viewBox", `0 0 ${w} ${h}`);
  }
  root.removeAttribute("width");
  root.removeAttribute("height");

  const parts = root
    .getAttribute("viewBox")!
    .split(/[\s,]+/)
    .map(Number);
  return { width: parts[2]!, height: parts[3]! };
}

// Isolate foreignObject HTML content from the app's global stylesheet by
// reverting every property to its UA/inherited value. Scoped with
// `:not(svg, svg *)` so it skips nested `<svg>` shapes: Chrome exposes SVG
// geometry (`r`, `cx`, `cy`, `d`, `width`, `height`, …) as CSS properties fed
// by presentation attributes, which sit at author-origin priority *below* a
// stylesheet rule — so an unscoped `all: revert` resets them to their initial
// value (0). That collapses comparison-table grids and icon circles to 0×0
// (SVG-namespaced but unpainted) while their inline-styled `<svg>` wrapper
// keeps its size. Excluding the svg subtree leaves those attributes intact.
export const FOREIGNOBJECT_CSS_ISOLATION = `foreignObject *:not(svg, svg *),
foreignObject *:not(svg, svg *)::before,
foreignObject *:not(svg, svg *)::after { all: revert; }`;

export function injectCssIsolation(root: SVGSVGElement): void {
  if (root.querySelector("foreignObject") === null) return;
  for (const s of root.querySelectorAll("style")) {
    if (s.textContent?.includes("all: revert")) return;
  }
  const style = root.ownerDocument!.createElementNS(
    "http://www.w3.org/2000/svg",
    "style",
  );
  style.textContent = FOREIGNOBJECT_CSS_ISOLATION;
  root.insertBefore(style, root.firstChild);
}

const URL_REF_ATTRS = [
  "clip-path",
  "mask",
  "filter",
  "fill",
  "stroke",
  "marker-start",
  "marker-mid",
  "marker-end",
];

export function scopeSvgIds(root: Element, suffix: string): void {
  // querySelectorAll doesn't include the root element itself, so collect manually
  const allWithId = [
    ...(root.getAttribute("id") ? [root] : []),
    ...root.querySelectorAll("[id]"),
  ];
  if (allWithId.length === 0) return;

  const idMap = new Map<string, string>();
  for (const el of allWithId) {
    const oldId = el.getAttribute("id")!;
    const newId = oldId + suffix;
    idMap.set(oldId, newId);
    el.setAttribute("id", newId);
  }

  const urlRefPattern = /url\(\s*#([^)]+)\s*\)/g;

  const allElements = root.querySelectorAll("*");
  for (const el of allElements) {
    for (const attr of ["href", "xlink:href"]) {
      const val = el.getAttribute(attr);
      if (val?.startsWith("#")) {
        const refId = val.slice(1);
        const newId = idMap.get(refId);
        if (newId) el.setAttribute(attr, `#${newId}`);
      }
    }

    for (const attr of URL_REF_ATTRS) {
      const val = el.getAttribute(attr);
      if (val && val.includes("url(")) {
        el.setAttribute(
          attr,
          val.replace(urlRefPattern, (match, id) => {
            const newId = idMap.get(id);
            return newId ? `url(#${newId})` : match;
          }),
        );
      }
    }

    const inlineStyle = el.getAttribute("style");
    if (inlineStyle && inlineStyle.includes("url(#")) {
      el.setAttribute(
        "style",
        inlineStyle.replace(urlRefPattern, (match, id) => {
          const newId = idMap.get(id);
          return newId ? `url(#${newId})` : match;
        }),
      );
    }
  }
}

let svgIdCounter = 0;

type PrepareSvgOptions = {
  componentAssets?: Record<string, string> | null;
  apiBase?: string;
  maxImageDimension?: number;
};

export function prepareSvg(
  str: string,
  opts?: PrepareSvgOptions,
): { root: SVGSVGElement; width: number; height: number } {
  const root = parseSvg(str);
  resolveAdRefs(
    root,
    opts?.componentAssets,
    opts?.apiBase,
    opts?.maxImageDimension,
  );
  const { width, height } = normalizeViewBox(root);
  injectCssIsolation(root);
  return { root, width, height };
}

/**
 * Prepare SVG for editor display while resolving ad: <use> refs before ad:
 * image refs so template-internal images can be resolved after symbol injection.
 */
export async function prepareSvgWithResolvedAdUseRefs(
  str: string,
  opts?: PrepareSvgOptions,
): Promise<{ root: SVGSVGElement; width: number; height: number }> {
  const root = parseSvg(str);
  await resolveAdUseRefs(root, opts?.componentAssets, opts?.apiBase);
  resolveAdRefs(
    root,
    opts?.componentAssets,
    opts?.apiBase,
    opts?.maxImageDimension,
  );
  const { width, height } = normalizeViewBox(root);
  injectCssIsolation(root);
  return { root, width, height };
}

export function hasAdUseRefs(str: string): boolean {
  try {
    const root = parseSvg(str);
    for (const useEl of Array.from(root.querySelectorAll("use"))) {
      const href =
        useEl.getAttribute("href") ?? useEl.getAttributeNS(XLINK_NS, "href");
      if (href?.startsWith("ad:")) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

export function prepareSvgForDisplay(
  str: string,
  opts?: PrepareSvgOptions,
): string {
  const { root } = prepareSvg(str, opts);
  scopeSvgIds(root, `__ad${svgIdCounter++}`);
  return serializeSvg(root);
}

export async function prepareSvgForDisplayWithResolvedAdUseRefs(
  str: string,
  opts?: PrepareSvgOptions,
): Promise<string> {
  const { root } = await prepareSvgWithResolvedAdUseRefs(str, opts);
  scopeSvgIds(root, `__ad${svgIdCounter++}`);
  return serializeSvg(root);
}

/** Parse SVG, apply a mutator function, serialize back. */
export function mutateSvg(
  svg: string,
  mutator: (root: SVGSVGElement) => void,
): string {
  const root = parseSvg(svg);
  mutator(root);
  return serializeSvg(root);
}

const EXCLUDED_TAGS = new Set(["defs", "style", "metadata"]);

/** Get top-level layer children (excluding defs, style, metadata). */
export function getLayerChildren(svgEl: SVGSVGElement | Element): Element[] {
  const children: Element[] = [];
  for (const child of svgEl.children) {
    if (!EXCLUDED_TAGS.has(child.tagName.toLowerCase())) {
      children.push(child);
    }
  }
  return children;
}

const FRONTEND_EDITING_DISABLED_ATTR = "data-disable-frontend-editing";

/** Returns true when an element (or one of its ancestors) is editor-disabled. */
export function isFrontendEditingDisabled(el: Element): boolean {
  if (el.closest(`[${FRONTEND_EDITING_DISABLED_ATTR}]`) !== null) {
    return true;
  }
  if (el.tagName.toLowerCase() === "use") {
    const href = el.getAttribute("href") ?? el.getAttributeNS(XLINK_NS, "href");
    if (
      href?.startsWith("ad:") ||
      href?.startsWith("#ad--") ||
      el.hasAttribute("data-ad-ref")
    ) {
      return true;
    }
  }
  return false;
}

/** Get layer children that remain editable in the frontend. */
export function getEditableLayerChildren(
  svgEl: SVGSVGElement | Element,
): Element[] {
  return getLayerChildren(svgEl).filter(
    (child) => !isFrontendEditingDisabled(child),
  );
}

/**
 * Marker for editor-locked layers. Matches the static (HTML) ad editor's
 * `data-ht-locked` attribute (see `html-ad-editor/serialize.ts`) so lock state
 * round-trips through the saved document identically in both editors.
 */
export const HT_LOCKED_ATTR = "data-ht-locked";

/**
 * Set (or, when `value` is null, remove) one declaration in an element's inline
 * `style` attribute, leaving the other declarations untouched.
 */
export function setInlineStyle(
  el: Element,
  prop: string,
  value: string | null,
): void {
  const style = el.getAttribute("style");
  const decls: string[] = [];
  if (style) {
    for (const decl of style.split(";")) {
      const idx = decl.indexOf(":");
      if (idx === -1) continue;
      const key = decl.slice(0, idx).trim();
      if (!key || key === prop) continue;
      decls.push(`${key}:${decl.slice(idx + 1).trim()}`);
    }
  }
  if (value !== null) decls.push(`${prop}:${value}`);
  if (decls.length === 0) el.removeAttribute("style");
  else el.setAttribute("style", decls.join(";"));
}

/** Whether a layer element is hidden. Mirrors the static editor's `display:none`. */
export function isElementHidden(el: Element): boolean {
  return readInlineStyle(el, "display")?.toLowerCase() === "none";
}

export function setElementHidden(el: Element, hidden: boolean): void {
  setInlineStyle(el, "display", hidden ? "none" : null);
}

/** Whether a layer element is locked (carries the `data-ht-locked` marker). */
export function isElementLocked(el: Element): boolean {
  return el.getAttribute(HT_LOCKED_ATTR) === "true";
}

export function setElementLocked(el: Element, locked: boolean): void {
  if (locked) el.setAttribute(HT_LOCKED_ATTR, "true");
  else el.removeAttribute(HT_LOCKED_ATTR);
}

/**
 * Collect the ids of editable layers hidden via inline `display:none`. Keyed by
 * the `id` attribute (preserved through save, unlike `data-ht-node-id`), which
 * is the same id space the layers panel and timeline toggle against.
 */
export function buildHiddenSet(root: SVGSVGElement): Set<string> {
  const result = new Set<string>();
  for (const layer of getEditableLayerChildren(root)) {
    if (isElementHidden(layer)) {
      const id = layer.getAttribute("id");
      if (id) result.add(id);
    }
  }
  return result;
}

/** Assign a unique ID to an element if it doesn't have one. */
export function ensureId(el: Element): string {
  let id = el.getAttribute("id");
  if (!id) {
    id = `ht-${crypto.randomUUID().slice(0, 8)}`;
    el.setAttribute("id", id);
  }
  return id;
}

/** Get the editor-stable ID for an element, preferring the editor-only ID. */
export function getElementStableId(el: Element): string {
  return el.getAttribute(EDITOR_NODE_ID_ATTR) ?? el.getAttribute("id") ?? "";
}

/** Ensure every non-root SVG element has a stable ID for DOM reconciliation. */
export function ensureEditorNodeIds(root: SVGSVGElement | Element): void {
  // Process the root element itself (querySelectorAll doesn't include it)
  if (!root.getAttribute(EDITOR_NODE_ID_ATTR)) {
    root.setAttribute(EDITOR_NODE_ID_ATTR, createEditorNodeId());
  }
  for (const el of root.querySelectorAll("*")) {
    if (!el.getAttribute(EDITOR_NODE_ID_ATTR)) {
      el.setAttribute(EDITOR_NODE_ID_ATTR, createEditorNodeId());
    }
  }
  // Ensure editable layer children have unique `id` attributes so that
  // lock/hidden state (keyed by id) doesn't collapse to a shared "" key.
  for (const child of getEditableLayerChildren(root as SVGSVGElement)) {
    ensureId(child);
  }
}

/** Remove all editor node IDs from an element tree so fresh IDs can be assigned. */
export function stripAllEditorNodeIds(root: Element): void {
  root.removeAttribute(EDITOR_NODE_ID_ATTR);
  for (const el of root.querySelectorAll(`[${EDITOR_NODE_ID_ATTR}]`)) {
    el.removeAttribute(EDITOR_NODE_ID_ATTR);
  }
}

/** Return the dynamic definition nodes used by the live preview. */
export function getDefinitionChildren(root: SVGSVGElement): Element[] {
  const nodes: Element[] = [];
  for (const child of Array.from(root.children)) {
    const tag = child.tagName.toLowerCase();
    if (tag === "defs") {
      nodes.push(...Array.from(child.children));
    } else if (tag === "style") {
      nodes.push(child);
    }
  }
  return nodes;
}

function getElementRenderKey(el: Element): string | null {
  const stableId = getElementStableId(el);
  return stableId ? `${el.tagName}:${stableId}` : null;
}

function setNamespacedAttr(target: Element, attr: Attr): void {
  if (attr.namespaceURI) {
    const localName = attr.localName ?? attr.name;
    const current = target.getAttributeNS(attr.namespaceURI, localName);
    if (current !== attr.value) {
      const qualifiedName = attr.prefix
        ? `${attr.prefix}:${localName}`
        : attr.name;
      target.setAttributeNS(attr.namespaceURI, qualifiedName, attr.value);
    }
    return;
  }

  if (target.getAttribute(attr.name) !== attr.value) {
    target.setAttribute(attr.name, attr.value);
  }
}

function removeNamespacedAttr(target: Element, attr: Attr): void {
  if (attr.namespaceURI) {
    target.removeAttributeNS(attr.namespaceURI, attr.localName ?? attr.name);
    return;
  }

  target.removeAttribute(attr.name);
}

function syncDomElement(target: Element, source: Element): void {
  const sourceAttrs = Array.from(source.attributes);
  const targetAttrs = Array.from(target.attributes);
  const sourceAttrNames = new Set(
    sourceAttrs.map((attr) =>
      attr.namespaceURI
        ? `${attr.namespaceURI}:${attr.localName ?? attr.name}`
        : attr.name,
    ),
  );

  for (const attr of targetAttrs) {
    const attrKey = attr.namespaceURI
      ? `${attr.namespaceURI}:${attr.localName ?? attr.name}`
      : attr.name;
    if (!sourceAttrNames.has(attrKey)) {
      removeNamespacedAttr(target, attr);
    }
  }

  for (const attr of sourceAttrs) {
    setNamespacedAttr(target, attr);
  }

  const sourceChildren = Array.from(source.children);
  if (sourceChildren.length === 0) {
    const sourceText = source.textContent ?? "";
    if (target.textContent !== sourceText) {
      target.textContent = sourceText;
    }
    return;
  }

  // The element-only reconciliation below strips all non-element child
  // nodes before syncing. That's fine for pure SVG containers (where text
  // nodes are just whitespace), but foreignObject text divs commonly have
  // bare text interleaved with styled <span> elements, e.g.:
  //
  //   <div>Hello <span style="color:red">World</span>!</div>
  //
  // Here .children is [span], so the element-only path would remove the
  // "Hello " and "!" text nodes — destroying visible content. Detect this
  // case and use a mixed-content sync that preserves text nodes.
  const hasMixedContent = Array.from(source.childNodes).some(
    (n) => n.nodeType === Node.TEXT_NODE && (n.textContent ?? "").length > 0,
  );

  if (hasMixedContent) {
    syncMixedChildNodes(target, source);
    return;
  }

  for (const child of Array.from(target.childNodes)) {
    if (child.nodeType !== Node.ELEMENT_NODE) {
      target.removeChild(child);
    }
  }

  reconcileElementChildren(target, sourceChildren);
}

/**
 * Sync all child nodes (text + elements) positionally for mixed-content
 * parents. Unlike reconcileElementChildren which only handles Element
 * children, this walks childNodes in order so bare text nodes between
 * elements are preserved. Reuses existing DOM elements of matching tag
 * to avoid unnecessary replacement (e.g. so images don't reload).
 */
function syncMixedChildNodes(target: Element, source: Element): void {
  const sourceNodes = Array.from(source.childNodes);
  const doc = target.ownerDocument;

  for (let i = 0; i < sourceNodes.length; i++) {
    const sn = sourceNodes[i]!;
    const tn = target.childNodes[i] as ChildNode | undefined;

    if (sn.nodeType === Node.TEXT_NODE) {
      if (tn && tn.nodeType === Node.TEXT_NODE) {
        if (tn.textContent !== sn.textContent) {
          tn.textContent = sn.textContent;
        }
      } else if (tn) {
        target.insertBefore(doc.createTextNode(sn.textContent ?? ""), tn);
      } else {
        target.appendChild(doc.createTextNode(sn.textContent ?? ""));
      }
    } else if (sn.nodeType === Node.ELEMENT_NODE) {
      const sourceEl = sn as Element;
      if (
        tn &&
        tn.nodeType === Node.ELEMENT_NODE &&
        (tn as Element).tagName === sourceEl.tagName
      ) {
        syncDomElement(tn as Element, sourceEl);
      } else if (tn) {
        target.insertBefore(doc.importNode(sourceEl, true), tn);
      } else {
        target.appendChild(doc.importNode(sourceEl, true));
      }
    }
  }

  // Remove excess target nodes
  while (target.childNodes.length > sourceNodes.length) {
    target.removeChild(target.lastChild!);
  }
}

/** Reconcile a rendered SVG subtree without replacing unchanged elements. */
export function reconcileElementChildren(
  targetParent: Element,
  sourceChildren: readonly Element[],
): void {
  const keyed = new Map<string, Element>();
  const unkeyed: Element[] = [];

  for (const child of Array.from(targetParent.children)) {
    const key = getElementRenderKey(child);
    if (key) {
      keyed.set(key, child);
    } else {
      unkeyed.push(child);
    }
  }

  const used = new Set<Element>();
  let unkeyedIndex = 0;
  let cursor: ChildNode | null = targetParent.firstChild;

  for (const sourceChild of sourceChildren) {
    const key = getElementRenderKey(sourceChild);
    const existing = key ? keyed.get(key) : unkeyed[unkeyedIndex++];
    let targetChild = existing;

    if (!targetChild || targetChild.tagName !== sourceChild.tagName) {
      const imported = targetParent.ownerDocument.importNode(
        sourceChild,
        true,
      ) as Element;
      targetParent.insertBefore(imported, cursor);
      targetChild = imported;
    } else {
      syncDomElement(targetChild, sourceChild);
      if (targetChild !== cursor) {
        targetParent.insertBefore(targetChild, cursor);
      }
    }

    used.add(targetChild);
    cursor = targetChild.nextSibling;
  }

  for (const child of Array.from(targetParent.children)) {
    if (!used.has(child)) {
      targetParent.removeChild(child);
    }
  }
}

/** Reconcile the dynamic defs and content containers in the live preview. */
export function reconcileRenderedSvg(
  defsRoot: SVGDefsElement,
  contentRoot: SVGGElement,
  sourceRoot: SVGSVGElement,
): void {
  reconcileElementChildren(defsRoot, getDefinitionChildren(sourceRoot));
  reconcileElementChildren(contentRoot, getLayerChildren(sourceRoot));
}

export interface ElementBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Get numeric attribute or default. Resolves percentage values against the
 *  element's owning SVG viewBox (x/cx/width/rx → viewBox width, others → height). */
export function getNumAttr(el: Element, name: string, fallback = 0): number {
  const v = el.getAttribute(name);
  if (v === null) return fallback;
  const n = Number.parseFloat(v);
  if (Number.isNaN(n)) return fallback;
  if (v.endsWith("%")) {
    const svg = el.closest("svg");
    const vb = svg
      ?.getAttribute("viewBox")
      ?.split(/[\s,]+/)
      .map(Number);
    if (!vb || vb.length < 4) return fallback;
    const horizontal =
      name === "x" || name === "cx" || name === "width" || name === "rx";
    return (n / 100) * (horizontal ? vb[2]! : vb[3]!);
  }
  return n;
}

/**
 * Resolve a numeric attribute that may be a percentage (e.g. "50%").
 * Percentages are resolved against the nearest ancestor SVG viewBox dimensions:
 * horizontal attributes (x, width, cx, rx, x1, x2) → viewBox width,
 * vertical attributes (y, height, cy, ry, y1, y2, r) → viewBox height.
 *
 * Use this for position/size attributes. Use `getNumAttr` for presentation
 * attributes like opacity or stroke-width where "%" has different semantics.
 */
export function resolveNumAttr(
  el: Element,
  name: string,
  fallback = 0,
): number {
  const v = el.getAttribute(name);
  if (v === null) return fallback;
  if (v.endsWith("%")) {
    const pct = Number.parseFloat(v);
    if (Number.isNaN(pct)) return fallback;
    const root = findSvgRoot(el);
    if (!root) return fallback;
    const vb = root
      .getAttribute("viewBox")
      ?.split(/[\s,]+/)
      .map(Number);
    if (!vb || vb.length < 4) return fallback;
    const isHorizontal =
      name === "x" ||
      name === "width" ||
      name === "cx" ||
      name === "rx" ||
      name === "x1" ||
      name === "x2";
    const ref = isHorizontal ? vb[2]! : vb[3]!;
    return (pct / 100) * ref;
  }
  const n = Number.parseFloat(v);
  return Number.isNaN(n) ? fallback : n;
}

/** Get string attribute. */
export function getAttr(el: Element, name: string, fallback = ""): string {
  return el.getAttribute(name) ?? fallback;
}

/** Set an attribute on an element. */
export function setAttr(el: Element, name: string, value: string): void {
  el.setAttribute(name, value);
}

/** Remove an attribute from an element. */
export function removeAttr(el: Element, name: string): void {
  el.removeAttribute(name);
}

/**
 * Compute the bounding box of a group from its children's bounds.
 * This is used for serialized SVG (DOMParser context, no getBBox).
 */
function computeGroupBounds(el: Element): ElementBounds {
  const storedW = el.getAttribute("data-bounds-w");
  const storedH = el.getAttribute("data-bounds-h");

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const child of el.children) {
    const b = getElementBounds(child);
    if (b.width === 0 && b.height === 0) continue;
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }

  if (!Number.isFinite(minX)) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  return {
    x: minX,
    y: minY,
    width: storedW !== null ? Number(storedW) : maxX - minX,
    height: storedH !== null ? Number(storedH) : maxY - minY,
  };
}

function getPathBounds(d: string): ElementBounds {
  try {
    const { x, y, width, height } = new SVGPathCommander(d).getBBox();
    return { x, y, width, height };
  } catch {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
}

/**
 * Find the `<rect>` inside a `<clipPath>` referenced by an element's clip-path attr.
 * Returns null if the element has no clip-path or no rect child.
 */
function findClipPathRect(el: Element, root: Element): Element | null {
  const clipRef = el.getAttribute("clip-path");
  if (!clipRef) return null;
  const idMatch = /url\(#([^)]+)\)/.exec(clipRef);
  if (!idMatch) return null;
  const clipEl = root.querySelector(`#${CSS.escape(idMatch[1]!)}`);
  if (!clipEl || clipEl.tagName.toLowerCase() !== "clippath") return null;
  return clipEl.querySelector("rect");
}

/** Walk up to find the nearest ancestor SVG root. */
function findSvgRoot(el: Element): Element | null {
  let current: Element | null = el.parentElement;
  while (current && current.tagName.toLowerCase() !== "svg") {
    current = current.parentElement;
  }
  return current;
}

/**
 * Find the crop clip-path rect element for an image by walking up to the SVG root.
 * Used by getElementBounds/setElementPosition/etc. which don't receive a root param.
 */
function findCropRect(el: Element): Element | null {
  if (el.tagName.toLowerCase() !== "image") return null;
  const root = findSvgRoot(el);
  if (!root) return null;
  return findClipPathRect(el, root);
}

/** Get element bounds dispatched by tagName. Uses resolveNumAttr to handle percentage values. */
export function getElementBounds(el: Element): ElementBounds {
  const tag = el.tagName.toLowerCase();
  switch (tag) {
    case "rect":
    case "foreignobject":
      return {
        x: resolveNumAttr(el, "x"),
        y: resolveNumAttr(el, "y"),
        width: resolveNumAttr(el, "width"),
        height: resolveNumAttr(el, "height"),
      };
    case "image": {
      // If image has a crop clip-path, return the clip rect bounds.
      // Crop rects are always created with absolute values by the editor.
      const cropRect = findCropRect(el);
      if (cropRect) {
        return {
          x: getNumAttr(cropRect, "x"),
          y: getNumAttr(cropRect, "y"),
          width: getNumAttr(cropRect, "width"),
          height: getNumAttr(cropRect, "height"),
        };
      }
      return {
        x: resolveNumAttr(el, "x"),
        y: resolveNumAttr(el, "y"),
        width: resolveNumAttr(el, "width"),
        height: resolveNumAttr(el, "height"),
      };
    }
    case "circle": {
      const cx = resolveNumAttr(el, "cx");
      const cy = resolveNumAttr(el, "cy");
      const r = resolveNumAttr(el, "r");
      return { x: cx - r, y: cy - r, width: r * 2, height: r * 2 };
    }
    case "ellipse": {
      const cx = resolveNumAttr(el, "cx");
      const cy = resolveNumAttr(el, "cy");
      const rx = resolveNumAttr(el, "rx");
      const ry = resolveNumAttr(el, "ry");
      return { x: cx - rx, y: cy - ry, width: rx * 2, height: ry * 2 };
    }
    case "path": {
      const d = el.getAttribute("d");
      return d ? getPathBounds(d) : { x: 0, y: 0, width: 0, height: 0 };
    }
    case "g":
      return computeGroupBounds(el);
    default:
      return {
        x: resolveNumAttr(el, "x"),
        y: resolveNumAttr(el, "y"),
        width: resolveNumAttr(el, "width"),
        height: resolveNumAttr(el, "height"),
      };
  }
}

/**
 * Strip editor-internal data attributes (e.g. resize bounds cache) before
 * persisting. Static-ad content can be either SVG (legacy/video) or HTML
 * (new html-ad-editor); detect the format and run the right pass.
 */
export function stripEditorMetadata(content: string): string {
  if (/^\s*<(div|html|!doctype)/i.test(content)) {
    return content.replace(/\s+data-ht-id="[^"]*"/g, "");
  }
  return mutateSvg(content, (root) => {
    for (const el of root.querySelectorAll(
      "[data-bounds-w], [data-bounds-h], [data-bounds-x], [data-bounds-y], [data-ht-node-id]",
    )) {
      el.removeAttribute("data-bounds-w");
      el.removeAttribute("data-bounds-h");
      el.removeAttribute("data-bounds-x");
      el.removeAttribute("data-bounds-y");
      el.removeAttribute(EDITOR_NODE_ID_ATTR);
    }
    // Restore ad: refs from data-ad-ref attributes (round-trip from resolveAdRefs)
    for (const el of root.querySelectorAll("[data-ad-ref]")) {
      const adRef = el.getAttribute("data-ad-ref");
      if (adRef) {
        el.setAttribute("href", adRef);
        el.removeAttribute("data-ad-ref");
      }
    }
    // Remove injected <defs data-ad-use-defs> blocks (from resolveAdUseRefs)
    for (const defs of root.querySelectorAll("defs[data-ad-use-defs]")) {
      defs.remove();
    }
  });
}

// ── Ancestor transform helpers ───────────────────────────────────

interface AffineMatrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

const IDENTITY: AffineMatrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

function multiplyAffine(m: AffineMatrix, n: AffineMatrix): AffineMatrix {
  return {
    a: m.a * n.a + m.c * n.b,
    b: m.b * n.a + m.d * n.b,
    c: m.a * n.c + m.c * n.d,
    d: m.b * n.c + m.d * n.d,
    e: m.a * n.e + m.c * n.f + m.e,
    f: m.b * n.e + m.d * n.f + m.f,
  };
}

function invertAffine(m: AffineMatrix): AffineMatrix {
  const det = m.a * m.d - m.b * m.c;
  if (Math.abs(det) < 1e-10) return { ...IDENTITY };
  return {
    a: m.d / det,
    b: -m.b / det,
    c: -m.c / det,
    d: m.a / det,
    e: (m.c * m.f - m.d * m.e) / det,
    f: (m.b * m.e - m.a * m.f) / det,
  };
}

function parseTransformAttr(attr: string): AffineMatrix {
  let m: AffineMatrix = { ...IDENTITY };
  const re = /(translate|scale|rotate|matrix)\s*\(([^)]+)\)/gi;
  for (let match = re.exec(attr); match !== null; match = re.exec(attr)) {
    const type = match[1]!.toLowerCase();
    const args = match[2]!.split(/[\s,]+/).map(Number);
    let t: AffineMatrix;
    switch (type) {
      case "translate":
        t = { ...IDENTITY, e: args[0] || 0, f: args[1] || 0 };
        break;
      case "scale": {
        const sx = args[0] || 1;
        const sy = args[1] ?? sx;
        t = { ...IDENTITY, a: sx, d: sy };
        break;
      }
      case "rotate": {
        const rad = ((args[0] || 0) * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const rotM: AffineMatrix = {
          a: cos,
          b: sin,
          c: -sin,
          d: cos,
          e: 0,
          f: 0,
        };
        if (args.length >= 3) {
          // rotate(angle, cx, cy) = translate(cx,cy) * rotate(angle) * translate(-cx,-cy)
          const cx = args[1] || 0;
          const cy = args[2] || 0;
          const pre: AffineMatrix = { ...IDENTITY, e: cx, f: cy };
          const post: AffineMatrix = { ...IDENTITY, e: -cx, f: -cy };
          t = multiplyAffine(multiplyAffine(pre, rotM), post);
        } else {
          t = rotM;
        }
        break;
      }
      case "matrix":
        t = {
          a: args[0] || 1,
          b: args[1] || 0,
          c: args[2] || 0,
          d: args[3] || 1,
          e: args[4] || 0,
          f: args[5] || 0,
        };
        break;
      default:
        continue;
    }
    m = multiplyAffine(m, t);
  }
  return m;
}

/**
 * Compute the cumulative ancestor transform for an element by walking
 * up and parsing transform attributes. Works on both live DOM and
 * DOMParser elements.
 */
export function getAncestorTransform(el: Element): AffineMatrix {
  const stack: AffineMatrix[] = [];
  let current = el.parentElement;
  while (current && current.tagName.toLowerCase() !== "svg") {
    const attr = current.getAttribute("transform");
    if (attr) {
      stack.push(parseTransformAttr(attr));
    }
    current = current.parentElement;
  }
  let m: AffineMatrix = { ...IDENTITY };
  for (let i = stack.length - 1; i >= 0; i--) {
    m = multiplyAffine(m, stack[i]!);
  }
  return m;
}

/**
 * Get the full transform (ancestors + element's own) as an SVG matrix() string.
 * Maps from the element's local coordinate space to the root SVG viewBox space.
 */
export function getFullElementTransform(el: Element): string {
  const ownAttr = el.getAttribute("transform");
  const own = ownAttr ? parseTransformAttr(ownAttr) : { ...IDENTITY };
  const ancestor = getAncestorTransform(el);
  const full = multiplyAffine(ancestor, own);
  return `matrix(${full.a},${full.b},${full.c},${full.d},${full.e},${full.f})`;
}

/**
 * Transform a point from an element's local coordinate space to viewBox space
 * using the element's full transform chain (ancestors + own).
 */
export function localPointToViewBox(
  el: Element,
  x: number,
  y: number,
): { x: number; y: number } {
  const ownAttr = el.getAttribute("transform");
  const own = ownAttr ? parseTransformAttr(ownAttr) : { ...IDENTITY };
  const ancestor = getAncestorTransform(el);
  const m = multiplyAffine(ancestor, own);
  return {
    x: m.a * x + m.c * y + m.e,
    y: m.b * x + m.d * y + m.f,
  };
}

/**
 * Convert a viewBox-space delta into the element's own local coordinate space,
 * accounting for BOTH ancestor transforms AND the element's own transform.
 * Use this when the target coordinates live inside the element's local space
 * (e.g. a clip-path rect on a rotated image).
 */
export function viewBoxToElementLocalDelta(
  el: Element,
  dx: number,
  dy: number,
): { dx: number; dy: number } {
  const ownAttr = el.getAttribute("transform");
  const own = ownAttr ? parseTransformAttr(ownAttr) : { ...IDENTITY };
  const ancestor = getAncestorTransform(el);
  const full = multiplyAffine(ancestor, own);
  const inv = invertAffine(full);
  return {
    dx: inv.a * dx + inv.c * dy,
    dy: inv.b * dx + inv.d * dy,
  };
}

/**
 * Convert a viewBox-space delta to the element's local coordinate space.
 * Accounts for ancestor scale/rotation — a no-op for translate-only ancestors.
 */
export function viewBoxToLocalDelta(
  el: Element,
  dx: number,
  dy: number,
): { dx: number; dy: number } {
  const inv = invertAffine(getAncestorTransform(el));
  return {
    dx: inv.a * dx + inv.c * dy,
    dy: inv.b * dx + inv.d * dy,
  };
}

/**
 * Convert a viewBox-space absolute point to the element's local coordinate space.
 */
export function viewBoxToLocalPoint(
  el: Element,
  x: number,
  y: number,
): { x: number; y: number } {
  const inv = invertAffine(getAncestorTransform(el));
  return {
    x: inv.a * x + inv.c * y + inv.e,
    y: inv.b * x + inv.d * y + inv.f,
  };
}

/**
 * Convert a viewBox-space size to the element's local coordinate space.
 */
export function viewBoxToLocalSize(
  el: Element,
  w: number,
  h: number,
): { w: number; h: number } {
  const inv = invertAffine(getAncestorTransform(el));
  return {
    w: Math.abs(inv.a) * w + Math.abs(inv.c) * h,
    h: Math.abs(inv.b) * w + Math.abs(inv.d) * h,
  };
}

// ── Live bounds ─────────────────────────────────────────────────

/**
 * Get bounds of a rendered SVG element in viewBox coordinates.
 * Uses getBBox for local bounds, then maps local → screen → viewBox
 * to account for parent group transforms.
 */
export function getLiveBounds(el: Element): ElementBounds {
  // For cropped images, getBBox returns the unclipped bounds (per SVG spec).
  // Return the crop rect bounds instead, transformed to viewBox coordinates.
  const cropRect = findCropRect(el);
  if (cropRect) {
    return getElementBounds(el);
  }

  const svgEl = el as SVGGraphicsElement;
  if ("getBBox" in svgEl && "getScreenCTM" in svgEl) {
    try {
      const bbox = svgEl.getBBox();
      if (bbox.width === 0 && bbox.height === 0) {
        return getElementBounds(el);
      }
      const svg = svgEl.ownerSVGElement;
      if (!svg) {
        return { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
      }
      const elScreen = svgEl.getScreenCTM();
      const svgScreen = svg.getScreenCTM();
      if (!elScreen || !svgScreen) {
        return { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
      }
      // local → screen → viewBox
      const toViewBox = svgScreen.inverse().multiply(elScreen);
      const pt = svg.createSVGPoint();
      const corners = [
        { x: bbox.x, y: bbox.y },
        { x: bbox.x + bbox.width, y: bbox.y },
        { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
        { x: bbox.x, y: bbox.y + bbox.height },
      ];
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const c of corners) {
        pt.x = c.x;
        pt.y = c.y;
        const tp = pt.matrixTransform(toViewBox);
        minX = Math.min(minX, tp.x);
        minY = Math.min(minY, tp.y);
        maxX = Math.max(maxX, tp.x);
        maxY = Math.max(maxY, tp.y);
      }
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    } catch {
      // fall through
    }
  }
  return getElementBounds(el);
}

/** Set the position of an element. */
export function setElementPosition(el: Element, x: number, y: number): void {
  const tag = el.tagName.toLowerCase();
  const rx = Math.round(x);
  const ry = Math.round(y);
  switch (tag) {
    case "rect":
    case "foreignobject":
      setAttr(el, "x", String(rx));
      setAttr(el, "y", String(ry));
      break;
    case "image": {
      const cropRectEl = findCropRect(el);
      if (cropRectEl) {
        // Move both image and crop rect by the same delta
        const dx = rx - getNumAttr(cropRectEl, "x");
        const dy = ry - getNumAttr(cropRectEl, "y");
        setAttr(cropRectEl, "x", String(rx));
        setAttr(cropRectEl, "y", String(ry));
        setAttr(el, "x", String(resolveNumAttr(el, "x") + dx));
        setAttr(el, "y", String(resolveNumAttr(el, "y") + dy));
      } else {
        setAttr(el, "x", String(rx));
        setAttr(el, "y", String(ry));
      }
      break;
    }
    case "circle":
    case "ellipse": {
      const bounds = getElementBounds(el);
      const cx = rx + bounds.width / 2;
      const cy = ry + bounds.height / 2;
      setAttr(el, "cx", String(Math.round(cx)));
      setAttr(el, "cy", String(Math.round(cy)));
      break;
    }
    case "path": {
      const d = el.getAttribute("d");
      if (d) {
        const bounds = getPathBounds(d);
        const dx = rx - bounds.x;
        const dy = ry - bounds.y;
        const newD = new SVGPathCommander(d)
          .transform({ translate: [dx, dy] })
          .toString();
        setAttr(el, "d", newD);
      }
      break;
    }
    case "g": {
      const bounds = getElementBounds(el);
      const gdx = rx - bounds.x;
      const gdy = ry - bounds.y;
      // gdx/gdy are already in group-local space (getElementBounds returns
      // local-space bounds for groups). Shift children directly instead of
      // calling shiftElement on the group itself, which would
      // inverse-transform the delta a second time through the group's own
      // transform (e.g. halving the delta for scale(2)).
      for (const child of el.children) {
        shiftElement(child, gdx, gdy);
      }
      break;
    }
    default:
      setAttr(el, "x", String(rx));
      setAttr(el, "y", String(ry));
      break;
  }
}

/** Set the size of an element. */
export function setElementSize(el: Element, w: number, h: number): void {
  const tag = el.tagName.toLowerCase();
  const rw = Math.round(w);
  const rh = Math.round(h);
  switch (tag) {
    case "rect":
    case "foreignobject":
      setAttr(el, "width", String(rw));
      setAttr(el, "height", String(rh));
      break;
    case "image": {
      const cropRectEl = findCropRect(el);
      if (cropRectEl) {
        // Scale both the image and crop rect proportionally so the
        // visible content stays aligned during resize.
        const oldCropW = getNumAttr(cropRectEl, "width") || 1;
        const oldCropH = getNumAttr(cropRectEl, "height") || 1;
        const sx = rw / oldCropW;
        const sy = rh / oldCropH;

        const cropX = getNumAttr(cropRectEl, "x");
        const cropY = getNumAttr(cropRectEl, "y");
        const imgX = resolveNumAttr(el, "x");
        const imgY = resolveNumAttr(el, "y");
        const imgW = resolveNumAttr(el, "width");
        const imgH = resolveNumAttr(el, "height");

        // Scale crop rect
        setAttr(cropRectEl, "width", String(rw));
        setAttr(cropRectEl, "height", String(rh));

        // Scale image relative to crop origin
        setAttr(el, "x", String(Math.round(cropX + (imgX - cropX) * sx)));
        setAttr(el, "y", String(Math.round(cropY + (imgY - cropY) * sy)));
        setAttr(el, "width", String(Math.round(imgW * sx)));
        setAttr(el, "height", String(Math.round(imgH * sy)));
      } else {
        setAttr(el, "width", String(rw));
        setAttr(el, "height", String(rh));
      }
      break;
    }
    case "circle": {
      const r = Math.round(Math.min(rw, rh) / 2);
      setAttr(el, "r", String(r));
      break;
    }
    case "ellipse":
      setAttr(el, "rx", String(Math.round(rw / 2)));
      setAttr(el, "ry", String(Math.round(rh / 2)));
      break;
    case "path": {
      const d = el.getAttribute("d");
      if (d) {
        const bounds = getPathBounds(d);
        if (bounds.width > 0 && bounds.height > 0) {
          const sx = rw / bounds.width;
          const sy = rh / bounds.height;
          // Scale around the path's top-left corner
          const newD = new SVGPathCommander(d)
            .transform({
              translate: [-bounds.x, -bounds.y],
            })
            .transform({
              scale: [sx, sy],
            })
            .transform({
              translate: [bounds.x, bounds.y],
            })
            .toString();
          setAttr(el, "d", newD);
        }
      }
      break;
    }
    default:
      setAttr(el, "width", String(rw));
      setAttr(el, "height", String(rh));
      break;
  }
}

/** Shift an element by dx, dy. */
export function shiftElement(el: Element, dx: number, dy: number): void {
  const tag = el.tagName.toLowerCase();
  if (tag === "g") {
    // Convert parent-space delta to group-local space, accounting for
    // the group's own transform (e.g. scale, rotate). Without this,
    // a scaled group would move less than expected because the delta
    // would be applied in the scaled coordinate space.
    const ownAttr = el.getAttribute("transform");
    let localDx = dx;
    let localDy = dy;
    if (ownAttr) {
      const m = parseTransformAttr(ownAttr);
      const inv = invertAffine(m);
      localDx = inv.a * dx + inv.c * dy;
      localDy = inv.b * dx + inv.d * dy;
    }
    for (const child of el.children) {
      shiftElement(child, localDx, localDy);
    }
    return;
  }
  // For elements with their own transform, convert the parent-space delta
  // to the element's local space so movement follows screen axes, not the
  // rotated/scaled axes.
  const ownAttr = el.getAttribute("transform");
  let localDx = dx;
  let localDy = dy;
  if (ownAttr) {
    const m = parseTransformAttr(ownAttr);
    const inv = invertAffine(m);
    localDx = inv.a * dx + inv.c * dy;
    localDy = inv.b * dx + inv.d * dy;
  }

  if (tag === "path") {
    const d = el.getAttribute("d");
    if (d) {
      const newD = new SVGPathCommander(d)
        .transform({ translate: [localDx, localDy] })
        .toString();
      setAttr(el, "d", newD);
    }
    return;
  }
  const bounds = getElementBounds(el);
  setElementPosition(el, bounds.x + localDx, bounds.y + localDy);
}

/** Extract the current rotation angle (degrees) from an element's transform attribute. */
export function getElementRotation(el: Element): number {
  const attr = el.getAttribute("transform");
  if (!attr) return 0;
  const match = /rotate\(\s*([-\d.]+)/.exec(attr);
  return match ? Number(match[1]) || 0 : 0;
}

/** Set or update the rotation of an element around its center. */
export function setElementRotation(el: Element, angleDeg: number): void {
  const attr = el.getAttribute("transform") ?? "";
  // Remove any existing rotate()
  const withoutRotate = attr.replace(/\s*rotate\([^)]*\)/g, "").trim();

  if (Math.abs(angleDeg) < 0.5) {
    // No rotation — just keep other transforms
    if (withoutRotate) {
      el.setAttribute("transform", withoutRotate);
    } else {
      el.removeAttribute("transform");
    }
    return;
  }

  // Compute center from bounds for rotate(angle, cx, cy)
  const bounds = getElementBounds(el);
  const cx = Math.round(bounds.x + bounds.width / 2);
  const cy = Math.round(bounds.y + bounds.height / 2);
  const rotateStr = `rotate(${Math.round(angleDeg)}, ${cx}, ${cy})`;

  if (withoutRotate) {
    el.setAttribute("transform", `${withoutRotate} ${rotateStr}`);
  } else {
    el.setAttribute("transform", rotateStr);
  }
}

/** Get the raw image element bounds (x/y/width/height attrs), ignoring any crop clip-path. */
export function getRawImageBounds(el: Element): ElementBounds {
  return {
    x: resolveNumAttr(el, "x"),
    y: resolveNumAttr(el, "y"),
    width: resolveNumAttr(el, "width"),
    height: resolveNumAttr(el, "height"),
  };
}

/** Read the crop clipPath rect for an image element. Returns null if uncropped. */
export function getImageCropRect(
  el: Element,
  root: Element,
): ElementBounds | null {
  const rect = findClipPathRect(el, root);
  if (!rect) return null;
  return {
    x: getNumAttr(rect, "x"),
    y: getNumAttr(rect, "y"),
    width: getNumAttr(rect, "width"),
    height: getNumAttr(rect, "height"),
  };
}

/** Create or update a crop clipPath for an image element. */
export function setImageCrop(
  el: Element,
  root: Element,
  crop: ElementBounds,
): void {
  const ns = "http://www.w3.org/2000/svg";
  const doc = root.ownerDocument;
  const defs = ensureDefs(root);

  // Check for existing clip-path
  const clipRef = el.getAttribute("clip-path");
  const existingId = clipRef ? /url\(#([^)]+)\)/.exec(clipRef)?.[1] : undefined;
  let clipEl: Element | null = existingId
    ? root.querySelector(`#${CSS.escape(existingId)}`)
    : null;

  if (clipEl) {
    // Update existing rect
    let rect = clipEl.querySelector("rect");
    if (!rect) {
      rect = doc.createElementNS(ns, "rect");
      clipEl.appendChild(rect);
    }
    rect.setAttribute("x", String(Math.round(crop.x)));
    rect.setAttribute("y", String(Math.round(crop.y)));
    rect.setAttribute("width", String(Math.round(crop.width)));
    rect.setAttribute("height", String(Math.round(crop.height)));
  } else {
    // Create new clipPath
    const id = `ht-crop-${crypto.randomUUID().slice(0, 8)}`;
    clipEl = doc.createElementNS(ns, "clipPath");
    clipEl.setAttribute("id", id);
    const rect = doc.createElementNS(ns, "rect");
    rect.setAttribute("x", String(Math.round(crop.x)));
    rect.setAttribute("y", String(Math.round(crop.y)));
    rect.setAttribute("width", String(Math.round(crop.width)));
    rect.setAttribute("height", String(Math.round(crop.height)));
    clipEl.appendChild(rect);
    defs.appendChild(clipEl);
    el.setAttribute("clip-path", `url(#${id})`);
  }
}

/** Read the current viewBox as {x, y, width, height}. Falls back to width/height attrs or 800x800. */
export function getCanvasBounds(root: Element): ElementBounds {
  const vb = root.getAttribute("viewBox");
  if (vb) {
    const parts = vb.split(/[\s,]+/).map(Number);
    if (parts.length >= 4 && parts[2]! > 0 && parts[3]! > 0) {
      return {
        x: parts[0]!,
        y: parts[1]!,
        width: parts[2]!,
        height: parts[3]!,
      };
    }
  }
  return {
    x: 0,
    y: 0,
    width: Number.parseFloat(root.getAttribute("width") ?? "800") || 800,
    height: Number.parseFloat(root.getAttribute("height") ?? "800") || 800,
  };
}

/** Update the SVG canvas dimensions by setting viewBox and width/height. */
export function setCanvasDimensions(
  root: Element,
  bounds: { x: number; y: number; width: number; height: number },
): void {
  const x = Math.round(bounds.x);
  const y = Math.round(bounds.y);
  const w = Math.round(Math.max(bounds.width, 1));
  const h = Math.round(Math.max(bounds.height, 1));
  root.setAttribute("viewBox", `${x} ${y} ${w} ${h}`);
  root.setAttribute("width", String(w));
  root.setAttribute("height", String(h));
}

/** Remove crop clipPath from an image element. */
export function removeImageCrop(el: Element, root: Element): void {
  const clipRef = el.getAttribute("clip-path");
  if (!clipRef) return;
  const idMatch = /url\(#([^)]+)\)/.exec(clipRef);
  if (idMatch) {
    const clipEl = root.querySelector(`#${CSS.escape(idMatch[1]!)}`);
    if (clipEl) clipEl.remove();
  }
  el.removeAttribute("clip-path");
}

export interface TextTarget {
  el: HTMLElement;
  foreignObject: Element;
}

/**
 * Walk past single-child wrapper divs to the content-bearing div.
 * A wrapper is a div whose only element child is another div and which has
 * no non-whitespace text of its own — typically a layout shell (e.g. a flex
 * container) around a styled inner div. Stops as soon as the current div has
 * text, multiple element children, or a non-div child. This lets
 * parseTextSegments find real text/span segments inside nested structures.
 */
function unwrapSingleDivWrappers(div: HTMLElement): HTMLElement {
  let current = div;
  while (true) {
    let hasOwnText = false;
    let elementChildCount = 0;
    let onlyDivChild: HTMLElement | null = null;
    for (const node of current.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        if ((node.textContent ?? "").trim() !== "") {
          hasOwnText = true;
          break;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        elementChildCount++;
        const el = node as Element;
        if (elementChildCount === 1 && el.tagName.toLowerCase() === "div") {
          onlyDivChild = el as HTMLElement;
        } else {
          onlyDivChild = null;
        }
      }
    }
    if (hasOwnText || elementChildCount !== 1 || !onlyDivChild) break;
    current = onlyDivChild;
  }
  return current;
}

/** Get the editable text target for a layer, if it is directly editable text. */
export function getTextTarget(layer: Element): TextTarget | null {
  if (isFrontendEditingDisabled(layer)) return null;

  const tag = layer.tagName.toLowerCase();

  if (
    tag === "foreignobject" &&
    layer.getAttribute("data-layer-type") === "text"
  ) {
    const div = layer.querySelector("div") as HTMLElement | null;
    if (div) {
      return { el: unwrapSingleDivWrappers(div), foreignObject: layer };
    }
  }

  return null;
}

/**
 * Trim XML-formatting whitespace from all foreignObject text divs.
 * DOMParser preserves leading/trailing whitespace (newlines, indentation)
 * that CSS white-space:normal collapses. Normalizing at parse time keeps
 * the document model clean so downstream code (e.g. contenteditable with
 * pre-wrap) doesn't see spurious whitespace.
 */
export function normalizeForeignObjectText(root: Element): void {
  for (const fo of root.querySelectorAll(
    'foreignObject[data-layer-type="text"]',
  )) {
    const div = fo.querySelector("div");
    if (!div) continue;
    // Trim leading/trailing whitespace text nodes without destroying
    // inner HTML structure (e.g. <span> elements with per-word styling).
    trimTextNodes(div);
  }
}

/**
 * Recursively trim leading whitespace from the first text node and
 * trailing whitespace from the last text node in a subtree, preserving
 * all element structure (spans, etc.).
 */
function trimTextNodes(node: Node): void {
  const first = firstTextNode(node);
  if (first) {
    first.textContent = (first.textContent ?? "").replace(/^[\s\n\r\t]+/, "");
  }
  const last = lastTextNode(node);
  if (last) {
    last.textContent = (last.textContent ?? "").replace(/[\s\n\r\t]+$/, "");
  }
}

function firstTextNode(node: Node): Text | null {
  if (node.nodeType === Node.TEXT_NODE) return node as Text;
  for (const child of node.childNodes) {
    const found = firstTextNode(child);
    if (found) return found;
  }
  return null;
}

function lastTextNode(node: Node): Text | null {
  if (node.nodeType === Node.TEXT_NODE) return node as Text;
  for (let i = node.childNodes.length - 1; i >= 0; i--) {
    const found = lastTextNode(node.childNodes[i]!);
    if (found) return found;
  }
  return null;
}

/** Get the inner `<div>` of a foreignObject text layer, or null. */
export function getTextDiv(el: Element): HTMLElement | null {
  return getTextTarget(el)?.el ?? null;
}

/**
 * True if this layer is a text layer whose inner text can be edited from the
 * properties panel — either a <foreignObject data-layer-type="text"> (rich,
 * with styling) or a bare SVG <text> element (plain-text fallback for older
 * ads generated before motion text standardized on foreignObject).
 */
export function isPlainEditableText(layer: Element): boolean {
  if (isFrontendEditingDisabled(layer)) return false;
  if (layer.tagName.toLowerCase() === "text") return true;
  return getTextTarget(layer) !== null;
}

/** Read a directly editable text layer as plain text. */
export function getTextLayerContent(layer: Element): string {
  if (isFrontendEditingDisabled(layer)) return "";
  if (layer.tagName.toLowerCase() === "text") {
    return layer.textContent ?? "";
  }
  const target = getTextTarget(layer);
  if (!target) return "";
  return target.el.textContent ?? "";
}

/** Replace the content of a directly editable text layer. */
export function setTextLayerContent(layer: Element, text: string): void {
  if (isFrontendEditingDisabled(layer)) return;
  if (layer.tagName.toLowerCase() === "text") {
    layer.textContent = text;
    return;
  }
  const target = getTextTarget(layer);
  if (!target) return;
  // If the div contains only plain text nodes (no spans/br/etc),
  // set textContent directly. Otherwise preserve the HTML structure
  // by only updating the first text-bearing node — this avoids
  // destroying per-word styling spans on every keystroke.
  if (hasRichContent(target.el)) {
    // Rich content: replace via innerHTML, stripping newlines since
    // text wraps based on the bounding box. This loses span styling,
    // which is acceptable — the agent can re-apply styling.
    const escaped = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, " ");
    (target.el as HTMLElement).innerHTML = escaped;
  } else {
    target.el.textContent = text;
  }
  autoSizeForeignObjectHeight(target.foreignObject);
}

/** Check if an element contains child elements (spans, etc.) */
function hasRichContent(el: Element): boolean {
  return el.querySelector("span, em, strong, b, i, u, a") !== null;
}

/** Check if a text layer has rich/inline-styled content (spans, etc.). */
export function isRichTextLayer(layer: Element): boolean {
  const target = getTextTarget(layer);
  if (!target) return false;
  return hasRichContent(target.el);
}

// ── Rich text segment editing ──

export interface TextSegment {
  type: "text" | "span";
  content: string;
  styles: {
    color?: string;
    fontWeight?: string;
    fontSize?: string;
    fontStyle?: string;
    textDecoration?: string;
  };
}

/**
 * Read an inline style property from an element's `style` attribute string.
 * This is more reliable than `el.style.prop` for XML-parsed elements where
 * the CSSStyleDeclaration may normalize values (e.g. hex → rgb()).
 */
function readInlineStyle(el: Element, prop: string): string | undefined {
  const attr = el.getAttribute("style");
  if (!attr) return undefined;
  const kebab = prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
  const re = new RegExp(`(?:^|;)\\s*${kebab}\\s*:\\s*([^;]+)`, "i");
  const match = re.exec(attr);
  return match?.[1]?.trim() || undefined;
}

const SANITIZE_ALLOWED_TAGS = new Set([
  "span",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "br",
]);

const SANITIZE_ALLOWED_CSS = new Set([
  "color",
  "font-family",
  "font-weight",
  "font-size",
  "font-style",
  "text-decoration",
  "letter-spacing",
  "line-height",
  "text-align",
]);

/**
 * Sanitize HTML from contenteditable to only allow known inline formatting
 * tags and CSS properties. Strips everything else while preserving text.
 */
export function sanitizeInlineHtml(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = html;
  sanitizeNode(template.content);
  return template.innerHTML;
}

function sanitizeNode(parent: Node): void {
  const children = Array.from(parent.childNodes);
  for (const node of children) {
    if (node.nodeType === Node.TEXT_NODE) continue;
    if (node.nodeType !== Node.ELEMENT_NODE) {
      parent.removeChild(node);
      continue;
    }
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (!SANITIZE_ALLOWED_TAGS.has(tag)) {
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      parent.removeChild(el);
      continue;
    }
    const attrNames = el.getAttributeNames();
    for (const attr of attrNames) {
      if (attr !== "style") {
        el.removeAttribute(attr);
      }
    }
    if (el.hasAttribute("style")) {
      const raw = el.getAttribute("style") ?? "";
      const filtered = raw
        .split(";")
        .filter((decl) => {
          const prop = decl.split(":")[0]?.trim().toLowerCase();
          return prop ? SANITIZE_ALLOWED_CSS.has(prop) : false;
        })
        .join(";")
        .trim();
      if (filtered) {
        el.setAttribute("style", filtered);
      } else {
        el.removeAttribute("style");
      }
    }
    sanitizeNode(el);
  }
}

/** Parse a text div's childNodes into a flat list of segments. */
export function parseTextSegments(div: HTMLElement): TextSegment[] {
  const segments: TextSegment[] = [];
  for (const node of div.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const content = node.textContent ?? "";
      if (content.length === 0) continue;
      segments.push({ type: "text", content, styles: {} });
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      if (tag === "br") {
        continue;
      }

      let fontWeight = readInlineStyle(el, "fontWeight");
      let fontStyle = readInlineStyle(el, "fontStyle");
      let textDecoration = readInlineStyle(el, "textDecoration");

      if (!fontWeight && (tag === "b" || tag === "strong")) {
        fontWeight = "700";
      }
      if (!fontStyle && (tag === "i" || tag === "em")) {
        fontStyle = "italic";
      }
      if (!textDecoration && tag === "u") {
        textDecoration = "underline";
      }

      segments.push({
        type: "span",
        content: el.textContent ?? "",
        styles: {
          color: readInlineStyle(el, "color"),
          fontWeight,
          fontSize: readInlineStyle(el, "fontSize"),
          fontStyle,
          textDecoration,
        },
      });
    }
  }
  return segments;
}

/**
 * Get the DOM node corresponding to the nth segment.
 * Skips empty text nodes to stay in sync with parseTextSegments.
 */
function getNthSegmentNode(
  div: HTMLElement,
  segmentIndex: number,
): Node | null {
  let idx = 0;
  for (const node of div.childNodes) {
    if (
      node.nodeType === Node.TEXT_NODE &&
      (node.textContent ?? "").length === 0
    ) {
      continue;
    }
    if (idx === segmentIndex) return node;
    idx++;
  }
  return null;
}

/** Update the text content of a specific segment. */
export function updateSegmentText(
  layer: Element,
  segmentIndex: number,
  text: string,
): void {
  const target = getTextTarget(layer);
  if (!target) return;
  const node = getNthSegmentNode(target.el, segmentIndex);
  if (!node) return;
  if (node.nodeType === Node.TEXT_NODE) {
    node.textContent = text;
  } else {
    (node as HTMLElement).textContent = text;
  }
  autoSizeForeignObjectHeight(target.foreignObject);
}

/** Update a CSS style property on a specific segment. Wraps plain text nodes in a span. */
export function updateSegmentStyle(
  layer: Element,
  segmentIndex: number,
  prop: string,
  value: string,
): void {
  const target = getTextTarget(layer);
  if (!target) return;
  let node = getNthSegmentNode(target.el, segmentIndex);
  if (!node) return;

  if (node.nodeType === Node.TEXT_NODE) {
    const doc = target.el.ownerDocument;
    const span = doc.createElementNS("http://www.w3.org/1999/xhtml", "span");
    span.textContent = node.textContent;
    node.parentNode!.replaceChild(span, node);
    node = span;
  }

  (node as HTMLElement).style[prop as any] = value;
  autoSizeForeignObjectHeight(target.foreignObject);
}

/** Remove a segment from a text layer. */
export function removeSegment(layer: Element, segmentIndex: number): void {
  const target = getTextTarget(layer);
  if (!target) return;
  const node = getNthSegmentNode(target.el, segmentIndex);
  if (!node) return;
  target.el.removeChild(node);
  autoSizeForeignObjectHeight(target.foreignObject);
}

/** Change the CSS text alignment of a foreignObject text layer. */
export function setTextAlignment(
  el: Element,
  newAnchor: "start" | "middle" | "end",
): void {
  const div = getTextDiv(el);
  if (!div) return;
  const alignMap: Record<string, string> = {
    start: "left",
    middle: "center",
    end: "right",
  };
  div.style.textAlign = alignMap[newAnchor] ?? "left";
}

/**
 * Get a display label for an element. Checks the metadata map first,
 * then falls back to text content or tag name.
 */
export function getElementLabel(
  el: Element,
  metadata?: Record<string, ElementMetadata>,
): string {
  const id = el.getAttribute("id");
  if (id && metadata?.[id]?.label) {
    return metadata[id].label!;
  }

  // Use data-label from Gemini if present (non-text, non-group elements)
  const dataLabel = el.getAttribute("data-label");
  if (dataLabel) {
    return dataLabel;
  }

  const tag = el.tagName.toLowerCase();
  if (
    tag === "foreignobject" &&
    el.getAttribute("data-layer-type") === "text"
  ) {
    const t = (el.textContent ?? "").trim();
    return t.length > 32 ? `${t.slice(0, 32)}…` : t || "Text";
  }

  return resolveElementTypeFromTag(el);
}

/** Derive an image label from its href (ad: ref or storage key). */
function getImageLabelFromHref(href: string): string {
  if (href.startsWith("ad:")) return "Image";
  if (href.startsWith("logo:")) return "Logo";
  return "Image";
}

/** Map data-asset-source values to display labels. */
const ASSET_SOURCE_LABELS: Record<string, string> = {
  logo: "Logo",
  product: "Product",
  reference: "Reference",
};

/** Resolve a type label from element tag and data-attributes. */
function resolveElementTypeFromTag(el: Element): string {
  const tag = el.tagName.toLowerCase();
  switch (tag) {
    case "foreignobject":
      return el.getAttribute("data-layer-type") === "text" ? "Text" : "Element";
    case "text":
      return "Text";
    case "image": {
      const href = el.getAttribute("href") ?? "";
      return getImageLabelFromHref(href);
    }
    case "rect":
      return "Rectangle";
    case "circle":
      return "Circle";
    case "ellipse":
      return "Ellipse";
    case "path":
      return "Path";
    case "g": {
      const gs = el.getAttribute("data-asset-source");
      if (gs && ASSET_SOURCE_LABELS[gs]) {
        return ASSET_SOURCE_LABELS[gs];
      }
      return "Group";
    }
    default:
      return "Element";
  }
}

/** Get a type label for the properties panel. */
export function getElementTypeLabel(el: Element): string {
  return resolveElementTypeFromTag(el);
}

/** Check if an event target is an editable element (input, textarea, contentEditable). */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target) return false;
  const el = target as HTMLElement;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
}

/** Check if an SVG element supports resize (groups do not). */
export function isResizableElement(el: Element): boolean {
  if (isFrontendEditingDisabled(el)) return false;
  const tag = el.tagName.toLowerCase();
  return tag !== "g";
}

const SHAPE_TAGS = new Set(["rect", "circle", "ellipse", "path"]);

/** Check if an SVG element is a shape that supports fill/border. */
export function isShapeElement(el: Element): boolean {
  return SHAPE_TAGS.has(el.tagName.toLowerCase());
}

/**
 * Resolve an element by a path of indices.
 * [2] → top-level layer 2
 * [2, 1] → child 1 of layer 2
 * [2, 1, 0] → grandchild 0 of child 1 of layer 2
 */
export function resolveElementByPath(
  root: Element,
  path: number[],
): Element | null {
  if (path.length === 0) return null;
  const layers = getEditableLayerChildren(root);
  let el: Element | undefined = layers[path[0]!];
  for (let i = 1; i < path.length; i++) {
    if (!el) return null;
    // Filter out defs/style/metadata at nested levels, same as at the root.
    const children = getLayerChildren(el);
    el = children[path[i]!];
  }
  return el ?? null;
}

// ── Gradient support ──────────────────────────────────────────────

export type FillType = "solid" | "linear" | "radial";

export interface GradientStop {
  offset: number; // 0–100
  color: string; // hex
}

export interface GradientInfo {
  type: FillType;
  color: string; // hex for solid fills
  stops: GradientStop[];
  angle: number; // degrees, for linear
  gradientId: string | null;
}

/** Parse fill info from an element, resolving url(#id) references. */
export function parseFillInfo(el: Element, root: Element): GradientInfo {
  const fill = el.getAttribute("fill") ?? "";
  const urlMatch = fill.match(/^url\(#(.+?)\)$/);
  if (!urlMatch) {
    return {
      type: "solid",
      color: fill.startsWith("#") ? fill : "#000000",
      stops: [],
      angle: 0,
      gradientId: null,
    };
  }

  const id = urlMatch[1]!;
  const gradEl = root.querySelector(`#${CSS.escape(id)}`);
  if (!gradEl) {
    return {
      type: "solid",
      color: "#000000",
      stops: [],
      angle: 0,
      gradientId: id,
    };
  }

  const tag = gradEl.tagName.toLowerCase();
  const stops = parseStops(gradEl);

  if (tag === "radialgradient") {
    return {
      type: "radial",
      color: "#000000",
      stops,
      angle: 0,
      gradientId: id,
    };
  }

  // linearGradient — compute angle from x1/y1/x2/y2
  const x1 = Number.parseFloat(gradEl.getAttribute("x1") ?? "0");
  const y1 = Number.parseFloat(gradEl.getAttribute("y1") ?? "0");
  const x2 = Number.parseFloat(gradEl.getAttribute("x2") ?? "1");
  const y2 = Number.parseFloat(gradEl.getAttribute("y2") ?? "0");
  const angle = coordsToAngle(x1, y1, x2, y2);

  return { type: "linear", color: "#000000", stops, angle, gradientId: id };
}

function parseStops(gradEl: Element): GradientStop[] {
  const stops: GradientStop[] = [];
  for (const child of gradEl.children) {
    if (child.tagName.toLowerCase() !== "stop") continue;
    const offsetStr = child.getAttribute("offset") ?? "0";
    const offset = offsetStr.endsWith("%")
      ? Number.parseFloat(offsetStr)
      : Number.parseFloat(offsetStr) * 100;
    const color = child.getAttribute("stop-color") ?? "#000000";
    stops.push({ offset, color });
  }
  return stops.sort((a, b) => a.offset - b.offset);
}

function coordsToAngle(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const rad = Math.atan2(dy, dx);
  const deg = (rad * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

/** Convert an angle (degrees) to normalized x1/y1/x2/y2 coords for linearGradient. */
export function angleToCoordsNormalized(deg: number): {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
} {
  const rad = (deg * Math.PI) / 180;
  const x1 = 0.5 - Math.cos(rad) * 0.5;
  const y1 = 0.5 - Math.sin(rad) * 0.5;
  const x2 = 0.5 + Math.cos(rad) * 0.5;
  const y2 = 0.5 + Math.sin(rad) * 0.5;
  return { x1, y1, x2, y2 };
}

/** Get or create <defs> in root SVG. */
export function ensureDefs(root: Element): Element {
  let defs = root.querySelector("defs");
  if (!defs) {
    const doc = root.ownerDocument;
    defs = doc.createElementNS("http://www.w3.org/2000/svg", "defs");
    root.insertBefore(defs, root.firstChild);
  }
  return defs;
}

/** Create a linearGradient element in <defs>. */
export function createLinearGradient(
  root: Element,
  id: string,
  angle: number,
  stops: GradientStop[],
): void {
  const defs = ensureDefs(root);
  const doc = root.ownerDocument;
  const ns = "http://www.w3.org/2000/svg";
  const grad = doc.createElementNS(ns, "linearGradient");
  grad.setAttribute("id", id);
  const coords = angleToCoordsNormalized(angle);
  grad.setAttribute("x1", String(coords.x1));
  grad.setAttribute("y1", String(coords.y1));
  grad.setAttribute("x2", String(coords.x2));
  grad.setAttribute("y2", String(coords.y2));
  appendStops(doc, grad, stops);
  defs.appendChild(grad);
}

/** Create a radialGradient element in <defs>. */
export function createRadialGradient(
  root: Element,
  id: string,
  stops: GradientStop[],
  cx = 0.5,
  cy = 0.5,
  r = 0.5,
): void {
  const defs = ensureDefs(root);
  const doc = root.ownerDocument;
  const ns = "http://www.w3.org/2000/svg";
  const grad = doc.createElementNS(ns, "radialGradient");
  grad.setAttribute("id", id);
  grad.setAttribute("cx", String(cx));
  grad.setAttribute("cy", String(cy));
  grad.setAttribute("r", String(r));
  appendStops(doc, grad, stops);
  defs.appendChild(grad);
}

function appendStops(
  doc: Document,
  grad: Element,
  stops: GradientStop[],
): void {
  const ns = "http://www.w3.org/2000/svg";
  for (const s of stops) {
    const stop = doc.createElementNS(ns, "stop");
    stop.setAttribute("offset", `${s.offset}%`);
    stop.setAttribute("stop-color", s.color);
    grad.appendChild(stop);
  }
}

/** Replace all <stop> children of a gradient element. */
export function updateGradientStops(
  gradientEl: Element,
  stops: GradientStop[],
): void {
  // Remove existing stops
  const existing = gradientEl.querySelectorAll("stop");
  for (const s of existing) s.remove();
  appendStops(gradientEl.ownerDocument, gradientEl, stops);
}

/** Update the angle of a linearGradient element. */
export function updateLinearGradientAngle(
  gradientEl: Element,
  angle: number,
): void {
  const coords = angleToCoordsNormalized(angle);
  gradientEl.setAttribute("x1", String(coords.x1));
  gradientEl.setAttribute("y1", String(coords.y1));
  gradientEl.setAttribute("x2", String(coords.x2));
  gradientEl.setAttribute("y2", String(coords.y2));
}

/** Remove a gradient from <defs> if no elements reference it. */
export function removeGradientIfUnused(root: Element, id: string): void {
  const refCount = root.querySelectorAll(`[fill="url(#${id})"]`).length;
  if (refCount === 0) {
    const grad = root.querySelector(`#${CSS.escape(id)}`);
    if (grad) grad.remove();
  }
}

/** Generate a unique gradient ID. */
export function generateGradientId(): string {
  return `grad-${crypto.randomUUID().slice(0, 8)}`;
}

/**
 * Auto-grow a foreignObject text layer's height to fit its text content.
 * Temporarily removes height/overflow constraints so the browser calculates
 * the natural wrapped height, then applies it to the foreignObject.
 * Only grows — never shrinks below the current height.
 */
export function autoSizeForeignObjectHeight(fo: Element): void {
  const div = getTextDiv(fo);
  if (!div) return;

  const text = div.textContent ?? "";
  if (!text.trim()) return;

  const currentHeight = resolveNumAttr(fo, "height");

  // Temporarily let the div size naturally so the browser computes wrapped height
  const prevHeight = div.style.height;
  const prevOverflow = div.style.overflow;
  div.style.height = "auto";
  div.style.overflow = "visible";

  const measuredHeight = div.scrollHeight;

  // Restore original styles
  div.style.height = prevHeight;
  div.style.overflow = prevOverflow;

  if (measuredHeight > currentHeight) {
    fo.setAttribute("height", String(measuredHeight));
  }
}

/**
 * Create a new text layer element and append it to the SVG root.
 * Returns the new foreignObject so callers can select it.
 */
const DEFAULT_VIEWBOX_SIZE = 1080;
const DEFAULT_FONT_FAMILY = "Inter";
const DEFAULT_FONT_SIZE = 24;
const DEFAULT_TEXT_HEIGHT = 60;
const TEXT_WIDTH_RATIO = 0.6;

export function createTextLayerElement(
  root: SVGSVGElement,
  fontFamily?: string,
): Element {
  const vb = root
    .getAttribute("viewBox")
    ?.split(/[\s,]+/)
    .map(Number) ?? [0, 0, DEFAULT_VIEWBOX_SIZE, DEFAULT_VIEWBOX_SIZE];
  const svgW = vb[2] ?? DEFAULT_VIEWBOX_SIZE;
  const svgH = vb[3] ?? DEFAULT_VIEWBOX_SIZE;
  const textWidth = Math.round(svgW * TEXT_WIDTH_RATIO);
  const textHeight = DEFAULT_TEXT_HEIGHT;
  const x = Math.round((svgW - textWidth) / 2);
  const y = Math.round((svgH - textHeight) / 2);

  const nodeId = createEditorNodeId();
  const layerId = `text-${crypto.randomUUID().slice(0, 8)}`;

  const fo = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "foreignObject",
  );
  fo.setAttribute("id", layerId);
  fo.setAttribute("x", String(x));
  fo.setAttribute("y", String(y));
  fo.setAttribute("width", String(textWidth));
  fo.setAttribute("height", String(textHeight));
  fo.setAttribute("data-layer-type", "text");
  fo.setAttribute(EDITOR_NODE_ID_ATTR, nodeId);

  const div = document.createElementNS("http://www.w3.org/1999/xhtml", "div");
  div.setAttribute(
    "style",
    `font-family: ${fontFamily ?? DEFAULT_FONT_FAMILY}, sans-serif; font-size: ${DEFAULT_FONT_SIZE}px; color: #000000; text-align: left; line-height: 1.2; margin: 0; padding: 0;`,
  );
  div.textContent = "New text";

  fo.appendChild(div);
  root.appendChild(fo);
  return fo;
}

/**
 * Create a new rectangle layer element and append it to the SVG root.
 * Returns the new <rect> so callers can select it.
 */
const RECT_WIDTH_RATIO = 0.4;
const RECT_HEIGHT_RATIO = 0.15;

export function createRectangleLayerElement(root: SVGSVGElement): Element {
  const vb = root
    .getAttribute("viewBox")
    ?.split(/[\s,]+/)
    .map(Number) ?? [0, 0, DEFAULT_VIEWBOX_SIZE, DEFAULT_VIEWBOX_SIZE];
  const svgW = vb[2] ?? DEFAULT_VIEWBOX_SIZE;
  const svgH = vb[3] ?? DEFAULT_VIEWBOX_SIZE;
  const rectWidth = Math.round(svgW * RECT_WIDTH_RATIO);
  const rectHeight = Math.round(svgH * RECT_HEIGHT_RATIO);
  const x = Math.round((svgW - rectWidth) / 2);
  const y = Math.round((svgH - rectHeight) / 2);

  const nodeId = createEditorNodeId();
  const layerId = `rect-${crypto.randomUUID().slice(0, 8)}`;

  const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  rect.setAttribute("id", layerId);
  rect.setAttribute("x", String(x));
  rect.setAttribute("y", String(y));
  rect.setAttribute("width", String(rectWidth));
  rect.setAttribute("height", String(rectHeight));
  rect.setAttribute("fill", "#cccccc");
  rect.setAttribute("data-label", "Rectangle");
  rect.setAttribute(EDITOR_NODE_ID_ATTR, nodeId);

  root.appendChild(rect);
  return rect;
}

/**
 * Create a new image layer element and append it to the SVG root.
 * Returns the new <image> so callers can select it.
 */
export function createImageLayerElement(
  root: SVGSVGElement,
  imageUrl: string,
): Element {
  const vb = root
    .getAttribute("viewBox")
    ?.split(/[\s,]+/)
    .map(Number) ?? [0, 0, DEFAULT_VIEWBOX_SIZE, DEFAULT_VIEWBOX_SIZE];
  const svgW = vb[2] ?? DEFAULT_VIEWBOX_SIZE;
  const svgH = vb[3] ?? DEFAULT_VIEWBOX_SIZE;
  const imageWidth = Math.round(svgW * 0.6);
  const imageHeight = Math.round(svgH * 0.6);
  const x = Math.round((svgW - imageWidth) / 2);
  const y = Math.round((svgH - imageHeight) / 2);

  const nodeId = createEditorNodeId();
  const layerId = `image-${crypto.randomUUID().slice(0, 8)}`;

  const img = document.createElementNS("http://www.w3.org/2000/svg", "image");
  img.setAttribute("id", layerId);
  img.setAttribute("x", String(x));
  img.setAttribute("y", String(y));
  img.setAttribute("width", String(imageWidth));
  img.setAttribute("height", String(imageHeight));
  img.setAttribute("href", imageUrl);
  img.setAttribute("preserveAspectRatio", "xMidYMid slice");
  img.setAttribute(EDITOR_NODE_ID_ATTR, nodeId);

  root.appendChild(img);
  return img;
}

// ─── Multi-select operations ──────────────────────────────────────────

/**
 * Group selected layers into a new `<g>` element. The group is inserted
 * at the position of the lowest-index element. Indices must be sorted ascending.
 */
export function groupElements(root: SVGSVGElement, indices: number[]): void {
  if (indices.length < 2) return;
  const sorted = [...indices].sort((a, b) => a - b);
  const children = getEditableLayerChildren(root);

  const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
  const groupId = `group-${crypto.randomUUID().slice(0, 8)}`;
  group.setAttribute("id", groupId);
  group.setAttribute(EDITOR_NODE_ID_ATTR, createEditorNodeId());

  // Collect elements to group (in SVG order)
  const elements = sorted.map((i) => children[i]).filter(Boolean) as Element[];
  if (elements.length < 2) return;

  // Insert group at the position of the first element
  const insertBefore = elements[0]!;
  insertBefore.parentElement?.insertBefore(group, insertBefore);

  // Move elements into the group
  for (const el of elements) {
    group.appendChild(el);
  }
}

/**
 * Ungroup a `<g>` element — move all children to the group's parent
 * at the group's position, then remove the empty group.
 */
export function ungroupElement(root: SVGSVGElement, index: number): void {
  const children = getEditableLayerChildren(root);
  const group = children[index];
  if (!group || group.tagName.toLowerCase() !== "g") return;

  const parent = group.parentElement;
  if (!parent) return;

  const groupTransform = group.getAttribute("transform") ?? "";

  const groupChildren = Array.from(group.children);
  for (const child of groupChildren) {
    if (groupTransform) {
      const existing = child.getAttribute("transform") ?? "";
      child.setAttribute("transform", `${groupTransform} ${existing}`.trim());
    }
    parent.insertBefore(child, group);
  }
  group.remove();
}

/**
 * Duplicate elements at the given indices. Each clone gets new IDs and
 * is inserted immediately after the original. Returns the indices of the
 * new elements (which may differ from originals due to insertion shifts).
 */
/**
 * Move a top-level layer to a new z-position. Used internally by
 * reorderNestedLayer for top-level paths.
 */
function reorderLayer(
  root: SVGSVGElement,
  index: number,
  direction: "forward" | "backward" | "front" | "back",
): number {
  const children = getEditableLayerChildren(root);
  const el = children[index];
  if (!el || !el.parentElement) return index;

  const parent = el.parentElement;
  const count = children.length;

  switch (direction) {
    case "forward":
      if (index >= count - 1) return index;
      // Move after the next sibling
      children[index + 1]?.after(el);
      return index + 1;
    case "backward":
      if (index <= 0) return index;
      // Move before the previous sibling
      children[index - 1]?.before(el);
      return index - 1;
    case "front":
      // Move to end of parent (top of stack)
      parent.appendChild(el);
      return count - 1;
    case "back": {
      // Move to beginning (before all editable children)
      const first = children[0];
      if (first && first !== el) parent.insertBefore(el, first);
      return 0;
    }
  }
}

/**
 * Duplicate the element at the given path. Works for both top-level
 * layers (path length 1) and nested elements within groups.
 * Returns the path of the newly created clone, or null on failure.
 */
export function duplicateElementAtPath(
  root: SVGSVGElement,
  path: number[],
): number[] | null {
  const el = resolveElementByPath(root, path);
  if (!el || !el.parentElement) return null;

  const clone = el.cloneNode(true) as Element;
  const suffix = crypto.randomUUID().slice(0, 6);
  scopeSvgIds(clone, suffix);
  stripAllEditorNodeIds(clone);
  ensureEditorNodeIds(clone);

  const transform = clone.getAttribute("transform") ?? "";
  clone.setAttribute("transform", `${transform} translate(10, 10)`.trim());

  el.after(clone);

  const parentPath = path.slice(0, -1);
  const childIndex = path[path.length - 1]!;
  return [...parentPath, childIndex + 1];
}

/**
 * Reorder a nested element within its parent group.
 * For top-level paths (length 1), delegates to reorderLayer.
 * Returns the updated full path.
 */
export function reorderNestedLayer(
  root: SVGSVGElement,
  path: number[],
  direction: "forward" | "backward" | "front" | "back",
): number[] {
  if (path.length <= 1) {
    const newIdx = reorderLayer(root, path[0]!, direction);
    return [newIdx];
  }

  const parentPath = path.slice(0, -1);
  const parent = resolveElementByPath(root, parentPath);
  if (!parent) return path;

  const childIndex = path[path.length - 1]!;
  const children = getLayerChildren(parent);
  const el = children[childIndex];
  if (!el) return path;

  const count = children.length;
  let newChildIndex = childIndex;

  switch (direction) {
    case "forward":
      if (childIndex >= count - 1) return path;
      children[childIndex + 1]?.after(el);
      newChildIndex = childIndex + 1;
      break;
    case "backward":
      if (childIndex <= 0) return path;
      children[childIndex - 1]?.before(el);
      newChildIndex = childIndex - 1;
      break;
    case "front":
      parent.appendChild(el);
      newChildIndex = count - 1;
      break;
    case "back": {
      const first = children[0];
      if (first && first !== el) parent.insertBefore(el, first);
      newChildIndex = 0;
      break;
    }
  }

  return [...parentPath, newChildIndex];
}

/**
 * Move an element to an absolute z-position among its siblings. `targetIndex`
 * is the index it occupies *after* the move, in document order (0 = painted
 * first / bottom-most), letting callers express a drop position directly
 * rather than as a run of relative steps. Returns the updated full path.
 */
export function moveNestedLayerToIndex(
  root: SVGSVGElement,
  path: number[],
  targetIndex: number,
): number[] {
  const parentPath = path.slice(0, -1);
  const parent =
    parentPath.length === 0 ? root : resolveElementByPath(root, parentPath);
  if (!parent) return path;

  // The root's editable children are the same list `resolveElementByPath`
  // indexes into, so paths stay consistent with the rest of the editor.
  const siblings =
    parentPath.length === 0
      ? getEditableLayerChildren(parent)
      : getLayerChildren(parent);

  const childIndex = path[path.length - 1]!;
  const el = siblings[childIndex];
  if (!el) return path;

  const insertAt = Math.max(0, Math.min(targetIndex, siblings.length - 1));
  if (insertAt === childIndex) return path;

  // `siblings` is the pre-move order, so anchoring the insertion on the
  // element currently at `insertAt` lands `el` exactly there: moving down it
  // goes after that anchor, moving up it goes before. Using an anchor element
  // rather than an offset also skips over any non-editable nodes (defs,
  // style, metadata) interleaved between siblings.
  if (insertAt > childIndex) {
    siblings[insertAt]!.after(el);
  } else {
    siblings[insertAt]!.before(el);
  }

  return [...parentPath, insertAt];
}
